import React, { useState, useEffect, useCallback } from 'react';
import { FiCalendar, FiActivity } from 'react-icons/fi';
import { FaCheckCircle, FaTimesCircle, FaRobot, FaSyncAlt, FaClipboardList, FaStar, FaChartBar, FaRedo, FaUndoAlt } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

interface ContestProgress {
  contestName: string;
  contestId: string;
  matches: MatchProgress[];
}

interface MatchProgress {
  matchId: string;
  matchName: string;
  localTeam?: string;
  visitorTeam?: string;
  scoreUpdated: boolean;
  fantasyPoints: boolean;
  state?: string; // Match state: Complete, Upcoming, Preview, etc.
}

const Tracker: React.FC = () => {
  const [series, setSeries] = useState<any[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [syncingScorecard, setSyncingScorecard] = useState(false);
  const [updatingFantasyPoints, setUpdatingFantasyPoints] = useState(false);
  const [contestsProgress, setContestsProgress] = useState<ContestProgress[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [updatingPointsContestId, setUpdatingPointsContestId] = useState<string | null>(null);
  const [revertingSeriesPoints, setRevertingSeriesPoints] = useState(false);
  const [revertingContestId, setRevertingContestId] = useState<string | null>(null);
  const mainScrollRef = React.useRef<HTMLDivElement>(null);

  // Get color classes based on match state
  const getMatchStateColors = (state?: string) => {
    switch (state) {
      case 'Complete':
        return {
          bg: 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40',
          border: 'border-emerald-400 dark:border-emerald-600',
          hover: 'hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-emerald-100 dark:hover:shadow-emerald-900/20',
          badge: 'bg-emerald-500 text-white',
        };
      case 'Upcoming':
        return {
          bg: 'bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/40 dark:to-blue-950/40',
          border: 'border-sky-400 dark:border-sky-600',
          hover: 'hover:border-sky-500 dark:hover:border-sky-500 hover:shadow-sky-100 dark:hover:shadow-sky-900/20',
          badge: 'bg-sky-500 text-white',
        };
      case 'Preview':
        return {
          bg: 'bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/40 dark:to-purple-950/40',
          border: 'border-violet-400 dark:border-violet-600',
          hover: 'hover:border-violet-500 dark:hover:border-violet-500 hover:shadow-violet-100 dark:hover:shadow-violet-900/20',
          badge: 'bg-violet-500 text-white',
        };
      case 'Live':
        return {
          bg: 'bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-950/40 dark:to-red-950/40',
          border: 'border-rose-500 dark:border-rose-600',
          hover: 'hover:border-rose-600 dark:hover:border-rose-500 hover:shadow-rose-100 dark:hover:shadow-rose-900/20',
          badge: 'bg-rose-500 text-white animate-pulse',
        };
      default:
        return {
          bg: 'bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/40 dark:to-gray-900/40',
          border: 'border-slate-300 dark:border-slate-600',
          hover: 'hover:border-slate-400 dark:hover:border-slate-500',
          badge: 'bg-slate-500 text-white',
        };
    }
  };

  // Load series
  const loadSeries = useCallback(async () => {
    try {
      setLoadingSeries(true);
      setError('');
      const response = await apiService.getSeries();
      const apiSeries = response?.data || response || [];

      // Filter to only show published series and map to consistent structure
      const publishedSeries = apiSeries
        .filter((s: any) => s.publish === true)
        .map((s: any) => ({
          seriesId: s.seriesId || s.id,
          name: s.name || s.title
        }));

      setSeries(publishedSeries);
    } catch (err) {
      console.error('Error loading series:', err);
      setError('Failed to load series');
    } finally {
      setLoadingSeries(false);
    }
  }, []);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const loadProgress = useCallback(async () => {
    if (!selectedSeries) return;

    try {
      setLoadingProgress(true);
      setError('');
      const data = await apiService.getSeriesProgress(selectedSeries);

      console.log('=== API Response Debug ===');
      console.log('Raw data:', data);
      console.log('Type:', typeof data);
      console.log('Is Array:', Array.isArray(data));

      // Transform the data into our format
      const transformed: ContestProgress[] = [];

      // Handle if data is wrapped in a contests property or is directly the array
      const contestsArray = data?.contests || data;

      console.log('Contests array:', contestsArray);
      console.log('Is contests array?', Array.isArray(contestsArray));

      if (Array.isArray(contestsArray)) {
        contestsArray.forEach((contest: any) => {
          console.log('\n--- Processing Contest ---');
          console.log('Contest:', contest);
          console.log('Contest Name:', contest.contestName);
          console.log('Match Progress:', contest.matchProgress);

          // Get matchProgress array
          const matchProgressArray = contest.matchProgress || [];

          if (Array.isArray(matchProgressArray)) {
            // Map each match in matchProgress
            const matchesArray: MatchProgress[] = matchProgressArray.map((match: any) => {
              console.log('  Processing match:', match);

              // Extract team short names (fallback to full name if short name not available)
              const localTeam = match?.localTeam?.teamSName ||
                               match?.localTeam?.teamName ||
                               match?.localTeam?.name ||
                               match?.localTeamName;

              const visitorTeam = match?.visitorTeam?.teamSName ||
                                 match?.visitorTeam?.teamName ||
                                 match?.visitorTeam?.name ||
                                 match?.visitorTeamName;

              console.log(`  Match ${match.matchId}: ${localTeam} vs ${visitorTeam}`);
              console.log(`  scoreUpdated: ${match.scoreUpdated}, fantasyPointsUpdated: ${match.fantasyPointsUpdated}`);
              console.log(`  state: ${match.state}`);

              return {
                matchId: String(match.matchId),
                matchName: `Match ${match.matchId}`,
                localTeam,
                visitorTeam,
                scoreUpdated: match.scoreUpdated || match.scoresUpdated || false,
                fantasyPoints: match.fantasyPointsUpdated || match.fantasyPoints || false,
                state: match.state || 'Unknown',
              };
            });

            console.log(`Contest "${contest.contestName}" → ${matchesArray.length} matches processed`);

            transformed.push({
              contestName: contest.contestName,
              contestId: contest.contestId,
              matches: matchesArray,
            });
          }
        });
      }

      console.log('\n=== Final Transformed Data ===');
      console.log('Total contests:', transformed.length);
      console.log('Contests:', transformed);

      setContestsProgress(transformed);
    } catch (err) {
      console.error('Error loading progress:', err);
      setError('Failed to load progress data');
      setContestsProgress([]);
    } finally {
      setLoadingProgress(false);
    }
  }, [selectedSeries]);

  // Load progress when series is selected
  useEffect(() => {
    if (selectedSeries) {
      loadProgress();
    }
  }, [selectedSeries, loadProgress]);

  // Process complete automation
  const handleProcessComplete = useCallback(async () => {
    if (!selectedSeries) return;

    try {
      setProcessingComplete(true);
      setError('');
      setSuccessMessage('');

      await apiService.processComplete(selectedSeries);

      setSuccessMessage('Tracker process completed successfully! Scorecards fetched, contests processed, and leaderboard updated.');

      // Refresh progress data after processing
      await loadProgress();
    } catch (err) {
      console.error('Error processing complete:', err);
      setError(err instanceof Error ? err.message : 'Failed to process automation');
    } finally {
      setProcessingComplete(false);
    }
  }, [selectedSeries, loadProgress]);

  // Sync scorecards
  const handleSyncScorecards = useCallback(async () => {
    if (!selectedSeries) return;

    try {
      setSyncingScorecard(true);
      setError('');
      setSuccessMessage('');

      await apiService.syncScorecards(selectedSeries);

      setSuccessMessage('Scorecards synced successfully!');

      // Refresh progress data after syncing
      await loadProgress();
    } catch (err) {
      console.error('Error syncing scorecards:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync scorecards');
    } finally {
      setSyncingScorecard(false);
    }
  }, [selectedSeries, loadProgress]);

  // Update fantasy points
  const handleUpdateFantasyPoints = useCallback(async () => {
    if (!selectedSeries) return;

    try {
      setUpdatingFantasyPoints(true);
      setError('');
      setSuccessMessage('');

      await apiService.updateFantasyPoints(selectedSeries);

      setSuccessMessage('Fantasy points updated successfully!');

      // Refresh progress data after updating
      await loadProgress();
    } catch (err) {
      console.error('Error updating fantasy points:', err);
      setError(err instanceof Error ? err.message : 'Failed to update fantasy points');
    } finally {
      setUpdatingFantasyPoints(false);
    }
  }, [selectedSeries, loadProgress]);

  // Update fantasy points for a specific contest
  const handleUpdatePointsByContest = useCallback(async (contestId: string, force: boolean = false) => {
    try {
      setUpdatingPointsContestId(contestId);
      setError('');
      setSuccessMessage('');

      const result = await apiService.updateFantasyPointsByContest(contestId, force ? { force: true } : undefined);
      const msg = typeof result === 'string' ? result : (result?.message || `Fantasy points ${force ? 'recomputed' : 'updated'} successfully!`);
      toast.success(msg);

      await loadProgress();
    } catch (err) {
      console.error('Error updating fantasy points by contest:', err);
      const errMsg = err instanceof Error ? err.message : 'Failed to update fantasy points';
      toast.error(errMsg);
    } finally {
      setUpdatingPointsContestId(null);
    }
  }, [loadProgress]);

  // Revert fantasy points for entire series
  const handleRevertSeriesPoints = useCallback(async () => {
    if (!selectedSeries) return;
    if (!window.confirm('Are you sure you want to revert ALL fantasy points for ALL contests in this series? This cannot be undone.')) return;

    try {
      setRevertingSeriesPoints(true);
      setError('');
      setSuccessMessage('');

      const result = await apiService.revertFantasyPointsBySeries(selectedSeries);
      const msg = typeof result === 'string' ? result : (result?.message || 'Fantasy points reverted for all contests in this series.');
      toast.success(msg);

      await loadProgress();
    } catch (err) {
      console.error('Error reverting series fantasy points:', err);
      const errMsg = err instanceof Error ? err.message : 'Failed to revert fantasy points';
      toast.error(errMsg);
    } finally {
      setRevertingSeriesPoints(false);
    }
  }, [selectedSeries, loadProgress]);

  // Revert fantasy points for a specific contest
  const handleRevertContestPoints = useCallback(async (contestId: string) => {
    if (!window.confirm('Are you sure you want to revert ALL fantasy points for this contest? This cannot be undone.')) return;

    try {
      setRevertingContestId(contestId);
      setError('');
      setSuccessMessage('');

      const result = await apiService.revertFantasyPointsByContest(contestId);
      const msg = typeof result === 'string' ? result : (result?.message || 'Fantasy points reverted for this contest.');
      toast.success(msg);

      await loadProgress();
    } catch (err) {
      console.error('Error reverting contest fantasy points:', err);
      const errMsg = err instanceof Error ? err.message : 'Failed to revert fantasy points';
      toast.error(errMsg);
    } finally {
      setRevertingContestId(null);
    }
  }, [loadProgress]);

  // Auto-scroll to first upcoming or last completed match
  useEffect(() => {
    if (contestsProgress.length > 0 && mainScrollRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const container = mainScrollRef.current;
        if (!container) return;

        const matchCards = container.querySelectorAll('[data-match-card]');
        let targetCard: HTMLElement | null = null;

        // Find first upcoming/preview match or last completed match
        let lastCompletedCard: HTMLElement | null = null;

        matchCards.forEach((card) => {
          const matchState = card.getAttribute('data-match-state');

          if (!targetCard && (matchState === 'Upcoming' || matchState === 'Preview')) {
            targetCard = card as HTMLElement;
          }

          if (matchState === 'Complete') {
            lastCompletedCard = card as HTMLElement;
          }
        });

        // Use upcoming if found, otherwise use last completed
        const cardToScrollTo: HTMLElement | null = targetCard || lastCompletedCard;

        if (cardToScrollTo) {
          const containerWidth = container.offsetWidth;
          const cardLeft = (cardToScrollTo as HTMLElement).offsetLeft;
          const cardWidth = (cardToScrollTo as HTMLElement).offsetWidth;
          const targetScrollLeft = cardLeft - (containerWidth / 2) + (cardWidth / 2);

          container.scrollLeft = targetScrollLeft;
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [contestsProgress]);

  const getStatusIcon = (status: boolean) => {
    if (status) {
      return <FaCheckCircle className="h-5 w-5 text-green-600 dark:text-success-500" />;
    }
    return <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500" />;
  };

  const getStatusBadge = (status: boolean) => {
    if (status) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-success-500/20 text-green-800 dark:text-success-500 border border-green-300 dark:border-success-500/30">
          <FaCheckCircle className="h-3 w-3 mr-1" />
          Completed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-danger-500/20 text-red-800 dark:text-danger-500 border border-red-300 dark:border-danger-500/30">
        <FaTimesCircle className="h-3 w-3 mr-1" />
        Pending
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-100 dark:bg-primary-500/20 rounded-lg border border-blue-200 dark:border-primary-500/30">
            <FaRobot className="h-6 w-6 text-blue-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tracker Progress</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {selectedSeries ? `Monitoring ${contestsProgress.length} contests` : 'Select a series to view tracker progress'}
            </p>
          </div>
        </div>
        {selectedSeries && (
          <button
            onClick={loadProgress}
            disabled={loadingProgress}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-primary-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50"
          >
            <FaSyncAlt className={`h-4 w-4 mr-2 ${loadingProgress ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* Series Selector */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-4">
        <label htmlFor="series-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <FiCalendar className="inline h-4 w-4 mr-1" />
          Select Series
        </label>
        <div className="flex items-end gap-3">
          <select
            id="series-select"
            value={selectedSeries}
            onChange={(e) => setSelectedSeries(e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">-- Select a series --</option>
            {loadingSeries ? (
              <option disabled>Loading...</option>
            ) : (
              series.map((s) => (
                <option key={s.seriesId} value={String(s.seriesId)}>
                  {s.name}
                </option>
              ))
            )}
          </select>
          {selectedSeries && (
            <div className="flex gap-2">
              <button
                onClick={handleSyncScorecards}
                disabled={syncingScorecard || loadingProgress}
                className="flex-shrink-0 inline-flex items-center px-4 py-2.5 bg-blue-600 dark:bg-primary-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaClipboardList className={`h-4 w-4 mr-2 ${syncingScorecard ? 'animate-spin' : ''}`} />
                {syncingScorecard ? 'Syncing...' : 'Sync Scorecards'}
              </button>
              <button
                onClick={handleUpdateFantasyPoints}
                disabled={updatingFantasyPoints || loadingProgress}
                className="flex-shrink-0 inline-flex items-center px-4 py-2.5 bg-amber-600 dark:bg-warning-600 text-white rounded-lg font-medium hover:bg-amber-700 dark:hover:bg-warning-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaStar className={`h-4 w-4 mr-2 ${updatingFantasyPoints ? 'animate-spin' : ''}`} />
                {updatingFantasyPoints ? 'Updating...' : 'Update Fantasy Points'}
              </button>
              <button
                onClick={handleProcessComplete}
                disabled={processingComplete || loadingProgress}
                className="flex-shrink-0 inline-flex items-center px-4 py-2.5 bg-emerald-600 dark:bg-success-600 text-white rounded-lg font-medium hover:bg-emerald-700 dark:hover:bg-success-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaRobot className={`h-4 w-4 mr-2 ${processingComplete ? 'animate-spin' : ''}`} />
                {processingComplete ? 'Updating...' : 'Fetch & Update'}
              </button>
              <button
                onClick={handleRevertSeriesPoints}
                disabled={revertingSeriesPoints || loadingProgress}
                className="flex-shrink-0 inline-flex items-center px-4 py-2.5 bg-red-600 dark:bg-danger-600 text-white rounded-lg font-medium hover:bg-red-700 dark:hover:bg-danger-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Revert ALL fantasy points for all contests in this series"
              >
                <FaUndoAlt className={`h-4 w-4 mr-2 ${revertingSeriesPoints ? 'animate-spin' : ''}`} />
                {revertingSeriesPoints ? 'Reverting...' : 'Revert All Points'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-emerald-50 dark:bg-success-500/20 border border-emerald-200 dark:border-success-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FaCheckCircle className="h-5 w-5 text-emerald-600 dark:text-success-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-800 dark:text-success-500">{successMessage}</div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loadingProgress && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading progress data...</p>
          </div>
        </div>
      )}

      {/* Contests Progress - Single Unified Scroll */}
      {!loadingProgress && selectedSeries && contestsProgress.length > 0 && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
          <div className="flex">
            {/* Contest Names - Fixed Left Column */}
            <div className="flex-shrink-0 w-48 bg-gradient-to-b from-blue-50 to-blue-100 dark:from-primary-500/10 dark:to-primary-600/10 border-r border-gray-200 dark:border-dark-300/40">
              {contestsProgress.map((contest) => (
                <div
                  key={contest.contestId}
                  className="p-4 border-b border-gray-200 dark:border-dark-300/40 last:border-b-0 flex flex-col justify-center h-40"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <FiActivity className="h-4 w-4 text-blue-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                      {contest.contestName}
                    </h3>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {contest.matches.length} {contest.matches.length === 1 ? 'Match' : 'Matches'}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      onClick={() => handleUpdatePointsByContest(contest.contestId, false)}
                      disabled={updatingPointsContestId === contest.contestId}
                      className="inline-flex items-center px-2 py-1 bg-green-600 text-white rounded text-[10px] font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Update points (new matches only)"
                    >
                      {updatingPointsContestId === contest.contestId ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                      ) : (
                        <FaChartBar className="h-3 w-3 mr-1" />
                      )}
                      Update
                    </button>
                    <button
                      onClick={() => handleUpdatePointsByContest(contest.contestId, true)}
                      disabled={updatingPointsContestId === contest.contestId}
                      className="inline-flex items-center px-2 py-1 bg-orange-600 text-white rounded text-[10px] font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Force recompute all matches"
                    >
                      {updatingPointsContestId === contest.contestId ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                      ) : (
                        <FaRedo className="h-3 w-3 mr-1" />
                      )}
                      Force
                    </button>
                    <button
                      onClick={() => handleRevertContestPoints(contest.contestId)}
                      disabled={revertingContestId === contest.contestId}
                      className="inline-flex items-center px-2 py-1 bg-red-600 text-white rounded text-[10px] font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Revert ALL fantasy points for this contest"
                    >
                      {revertingContestId === contest.contestId ? (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                      ) : (
                        <FaUndoAlt className="h-3 w-3 mr-1" />
                      )}
                      Revert
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* All Matches - Single Horizontal Scroll */}
            <div ref={mainScrollRef} className="flex-1 overflow-x-auto">
              <div className="p-4 space-y-4">
                {contestsProgress.map((contest) => (
                  <div key={contest.contestId} className="h-40 flex items-center">
                    <div className="flex gap-3">
                      {contest.matches.map((match) => {
                        const colors = getMatchStateColors(match.state);
                        return (
                        <div
                          key={match.matchId}
                          data-match-card
                          data-match-state={match.state}
                          className={`flex-shrink-0 w-64 ${colors.bg} rounded-xl border-2 ${colors.border} p-3 ${colors.hover} transition-all hover:shadow-lg hover:scale-[1.02]`}
                        >
                        {/* Match ID and State Badge */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-mono font-bold text-gray-800 dark:text-gray-100">
                            #{match.matchId}
                          </div>
                          {match.state && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${colors.badge} font-semibold shadow-sm`}>
                              {match.state}
                            </span>
                          )}
                        </div>

                        {/* Teams - Next Line */}
                        {match.localTeam && match.visitorTeam ? (
                          <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center justify-center gap-2">
                            <span className="truncate max-w-[100px] bg-white/60 dark:bg-black/20 px-2 py-1 rounded" title={match.localTeam}>
                              {match.localTeam}
                            </span>
                            <span className="text-xs font-bold text-gray-500 dark:text-gray-400">vs</span>
                            <span className="truncate max-w-[100px] bg-white/60 dark:bg-black/20 px-2 py-1 rounded" title={match.visitorTeam}>
                              {match.visitorTeam}
                            </span>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 dark:text-gray-500 italic mb-3 text-center">
                            Teams N/A
                          </div>
                        )}

                        {/* Status Indicators */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className={`flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md font-semibold text-xs transition-all ${
                            match.scoreUpdated
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-orange-500 hover:bg-orange-600 text-white'
                          }`}>
                            {match.scoreUpdated ? (
                              <FaCheckCircle className="h-3 w-3 flex-shrink-0" />
                            ) : (
                              <FaTimesCircle className="h-3 w-3 flex-shrink-0" />
                            )}
                            <span>Score</span>
                          </div>
                          <div className={`flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md font-semibold text-xs transition-all ${
                            match.fantasyPoints
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-orange-500 hover:bg-orange-600 text-white'
                          }`}>
                            {match.fantasyPoints ? (
                              <FaCheckCircle className="h-3 w-3 flex-shrink-0" />
                            ) : (
                              <FaTimesCircle className="h-3 w-3 flex-shrink-0" />
                            )}
                            <span>Points</span>
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}

      {/* Empty State - When series selected but no data */}
      {!loadingProgress && selectedSeries && contestsProgress.length === 0 && !error && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-gray-100 dark:bg-dark-300/40 rounded-full flex items-center justify-center mb-4">
              <FiActivity className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Progress Data</h3>
            <p className="text-gray-600 dark:text-gray-400">No automation progress data found for this series</p>
          </div>
        </div>
      )}

      {/* No Series Selected State */}
      {!selectedSeries && (
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 bg-blue-100 dark:bg-primary-500/20 rounded-full flex items-center justify-center mb-4 border border-blue-200 dark:border-primary-500/30">
              <FiCalendar className="h-8 w-8 text-blue-500 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Select a series</h3>
            <p className="text-gray-600 dark:text-gray-400">Choose a series from the dropdown to view automation progress</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tracker;
